import React, { useMemo, useState } from 'react';
import { authToken } from '../../authToken';
import TimePicker from '../../components/TimePicker';
import { API_BASE_URL } from '../../config';

const ENDPOINT_HOST = API_BASE_URL;
const ENDPOINT_FOOD = `${ENDPOINT_HOST}/foodintake`;

function toIntOrNull(s) {
  if (s === '' || s === null || s === undefined) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  return n;
}

export default function FoodIntakeEditPanel({ item, onClose = null }) {
  const [selectedTimestamp, setSelectedTimestamp] = useState(Number(item?.timestamp) || Date.now());
  const [name, setName] = useState(item?.name ?? '');
  const [carbs, setCarbs] = useState(item?.carbs ?? 0);
  const [sugarInCarbs, setSugarInCarbs] = useState(item?.sugarInCarbs ?? 0);
  const [protein, setProtein] = useState(item?.protein ?? 0);
  const [fat, setFat] = useState(item?.fat ?? 0);
  const [kcal, setKcal] = useState(item?.kcal ?? 0);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit = useMemo(() => {
    const nm = (name || '').trim();
    return (
      nm.length > 0 &&
      toIntOrNull(carbs) !== null &&
      toIntOrNull(sugarInCarbs) !== null &&
      toIntOrNull(protein) !== null &&
      toIntOrNull(fat) !== null &&
      toIntOrNull(kcal) !== null
    );
  }, [name, carbs, sugarInCarbs, protein, fat, kcal]);

  const handleCancel = () => {
    if (onClose) onClose(false);
  };

  const handleOk = async () => {
    setError(null);

    const id = item?.id;
    if (id === null || id === undefined) {
      setError('Missing item id.');
      return;
    }

    const nm = (name || '').trim();
    if (!nm) {
      setError('Name is required.');
      return;
    }

    const carbsNum = toIntOrNull(carbs);
    const sugarInCarbsNum = toIntOrNull(sugarInCarbs);
    const proteinNum = toIntOrNull(protein);
    const fatNum = toIntOrNull(fat);
    const kcalNum = toIntOrNull(kcal);

    if (carbsNum === null || sugarInCarbsNum === null || proteinNum === null || fatNum === null || kcalNum === null) {
      setError('Carbs / sugar-in-carbs / protein / fat / kcal must be integers.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${ENDPOINT_FOOD}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': authToken } : {})
        },
        body: JSON.stringify({
          id,
          name: nm,
          carbs: carbsNum,
          sugarInCarbs: sugarInCarbsNum,
          protein: proteinNum,
          fat: fatNum,
          kcal: kcalNum,
          timestamp: selectedTimestamp,
        }),
      });

      if (response.ok) {
        if (onClose) onClose(true);
      } else {
        const errorData = await response.text();
        setError(`Failed to update food intake: ${errorData} (${response.status})`);
      }
    } catch (e) {
      console.error('Failed to update food intake:', e);
      setError('Failed to update food intake. Please check your connection.');
    } finally {
      setTimeout(() => setIsSubmitting(false), 800);
    }
  };

  const inputStyle = {
    width: '100%',
    fontSize: '1.05rem',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #ccc',
    boxSizing: 'border-box'
  };

  const buttonStyle = {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    minWidth: '100px'
  };

  const fieldLabelStyle = {
    fontSize: 12,
    fontWeight: 800,
    color: '#444',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.02em'
  };

  const fieldWrapStyle = {
    display: 'flex',
    flexDirection: 'column'
  };

  return (
    <div style={{
      padding: '10px 0',
      maxWidth: '420px',
      margin: '0 auto',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    }}>
      <div>
        <h3 style={{ margin: '0 0 16px 0', textAlign: 'center' }}>✏️ Edit Food Intake</h3>

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            textAlign: 'center',
            border: '1px solid #f5c6cb'
          }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: 'grid', gap: 12, marginBottom: 14 }}>
          <div style={fieldWrapStyle}>
            <div style={fieldLabelStyle}>Name</div>
            <input
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={inputStyle}
              autoFocus
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>Carbs</div>
              <input type="number" placeholder="g" value={carbs} onChange={(e) => setCarbs(e.target.value)} style={inputStyle} />
            </div>
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>Sugar in carbs</div>
              <input type="number" placeholder="g" value={sugarInCarbs} onChange={(e) => setSugarInCarbs(e.target.value)} style={inputStyle} />
            </div>
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>Protein</div>
              <input type="number" placeholder="g" value={protein} onChange={(e) => setProtein(e.target.value)} style={inputStyle} />
            </div>
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>Fat</div>
              <input type="number" placeholder="g" value={fat} onChange={(e) => setFat(e.target.value)} style={inputStyle} />
            </div>
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>kcal</div>
              <input type="number" placeholder="kcal" value={kcal} onChange={(e) => setKcal(e.target.value)} style={inputStyle} />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <TimePicker initialTimestamp={selectedTimestamp} onTimeChange={setSelectedTimestamp} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: 'auto' }}>
        <button
          onClick={handleCancel}
          disabled={isSubmitting}
          style={{
            ...buttonStyle,
            backgroundColor: '#6c757d',
            color: 'white',
            opacity: isSubmitting ? 0.6 : 1
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleOk}
          disabled={isSubmitting || !canSubmit}
          style={{
            ...buttonStyle,
            backgroundColor: '#00a2ff',
            color: 'white',
            opacity: (isSubmitting || !canSubmit) ? 0.6 : 1
          }}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
